
import { useState, useEffect, useCallback } from 'react';
import { Bike } from '@/services/types';
import { toast } from '@/components/ui/sonner';
import { SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY } from '@/integrations/supabase/config';
import { fetchAllBikes } from '../services/bikePaginationService';

export const useBikesData = () => {
  const [bikes, setBikes] = useState<Bike[]>([]);
  const [allBikes, setAllBikes] = useState<Bike[]>([]);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [selectedBike, setSelectedBike] = useState<Bike | null>(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [movementFilter, setMovementFilter] = useState<string | null>(null);
  const [searchQuery, setSearchQueryState] = useState<string>('');
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [dataSource, setDataSource] = useState<'supabase' | 'mock'>('supabase');
  const [totalCount, setTotalCount] = useState(0);
  
  // Основная функция загрузки данных
  const loadBikesData = useCallback(async () => {
    console.log('=== ЗАГРУЗКА ДАННЫХ ВЕЛОСИПЕДОВ ===');
    
    try {
      setLoading(true);
      setStatsLoading(true);
      setConnectionError(null);
      
      // Используем новый метод загрузки всех данных
      const result = await fetchAllBikes();
      
      let bikesToShow = result.bikes;
      
      // Применяем поисковый фильтр
      if (searchQuery && searchQuery.trim()) {
        const searchLower = searchQuery.toLowerCase();
        bikesToShow = result.bikes.filter(bike => {
          return (
            bike.serialNumber?.toLowerCase().includes(searchLower) ||
            bike.model?.toLowerCase().includes(searchLower) ||
            bike.gps_tag?.toLowerCase().includes(searchLower) ||
            bike.courierName?.toLowerCase().includes(searchLower) ||
            bike.amo_number?.toLowerCase().includes(searchLower) ||
            bike.rental_human_number?.toLowerCase().includes(searchLower)
          );
        });
        console.log(`Отфильтровано по поиску: ${bikesToShow.length} из ${result.bikes.length}`);
      }
      
      // Применяем фильтры статуса
      let filteredBikes = bikesToShow;
      
      if (statusFilter && statusFilter !== 'all') {
        filteredBikes = filteredBikes.filter(bike => bike.status === statusFilter);
      }
      
      if (movementFilter && movementFilter !== 'all') {
        if (movementFilter === 'blocked') {
          filteredBikes = filteredBikes.filter(bike => bike.movement_blocked);
        } else if (movementFilter === 'speed_limited') {
          filteredBikes = filteredBikes.filter(bike => bike.speed_limit && bike.speed_limit > 0);
        } else if (movementFilter === 'unrestricted') {
          filteredBikes = filteredBikes.filter(bike => !bike.movement_blocked && (!bike.speed_limit || bike.speed_limit === 0));
        }
      }
      
      console.log(`Финальное количество велосипедов: ${filteredBikes.length}`);
      
      setBikes(filteredBikes);
      setAllBikes(result.bikes); // Сохраняем все велосипеды для статистики
      setConnectionError(result.connectionError);
      setDataSource(result.dataSource);
      setTotalCount(result.bikes.length);
      
    } catch (error: any) {
      console.error('Ошибка загрузки данных велосипедов:', error);
      setConnectionError(error.message);
      setBikes([]);
      setAllBikes([]);
    } finally {
      setLoading(false);
      setStatsLoading(false);
    }
  }, [statusFilter, movementFilter, searchQuery]);

  const handleSearchQueryChange = (query: string) => {
    setSearchQueryState(query);
  };

  const handleRefreshData = useCallback(async () => {
    await loadBikesData();
  }, [loadBikesData]);

  const handleAddBike = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);
    
    const newBike = {
      serial_number: formData.get('serialNumber') as string,
      model: formData.get('model') as string,
      status: formData.get('status') as 'available' | 'in_use' | 'maintenance' | 'retired',
      mileage: parseInt(formData.get('mileage') as string) || 0,
      last_service_date: new Date().toISOString(),
      gps_tag: formData.get('gpsTag') as string || null,
      amo_number: formData.get('amoNumber') as string || null
    };
    
    console.log('=== ДОБАВЛЕНИЕ НОВОГО ВЕЛОСИПЕДА ===');
    console.log('Данные нового велосипеда:', newBike);
    
    try {
      const response = await fetch(`${SUPABASE_URL}/rest/v1/bikes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_PUBLISHABLE_KEY,
          'Authorization': `Bearer ${SUPABASE_PUBLISHABLE_KEY}`,
          'Prefer': 'return=representation'
        },
        body: JSON.stringify(newBike)
      });
      
      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(`Ошибка при добавлении: ${response.status} ${errorData}`);
      }
      
      await response.json();
      toast.success('Велосипед добавлен успешно!');
      handleRefreshData();
      setShowAddDialog(false);
    } catch (error: any) {
      console.error('Ошибка при добавлении велосипеда:', error);
      toast.error(`Не удалось добавить велосипед: ${error.message}`);
    }
  };

  const handleEditBike = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedBike) return;
    
    const form = event.currentTarget;
    const formData = new FormData(form);
    
    const model = formData.get('editModel') as string;
    const status = formData.get('editStatus') as 'available' | 'in_use' | 'maintenance' | 'retired';
    const gps_tag = formData.get('editGpsTag') as string || null;
    const mileage = formData.get('editMileage') as string || '0';
    const amo_number = formData.get('editAmoNumber') as string || null;
    
    const movementBlockedRaw = formData.get('movement_blocked') as string;
    const movement_blocked = movementBlockedRaw === 'true';
    
    const speedLimitRaw = formData.get('speed_limit') as string;
    const speed_limit = speedLimitRaw && speedLimitRaw !== '' ? parseInt(speedLimitRaw) : null;
    
    const updatedFields = {
      model,
      status,
      gps_tag,
      mileage: parseInt(mileage) || 0,
      movement_blocked,
      speed_limit,
      amo_number
    };
    
    try {
      const url = `${SUPABASE_URL}/rest/v1/bikes?id=eq.${selectedBike.id}`;
      
      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_PUBLISHABLE_KEY,
          'Authorization': `Bearer ${SUPABASE_PUBLISHABLE_KEY}`,
          'Prefer': 'return=minimal'
        },
        body: JSON.stringify(updatedFields)
      });
      
      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(`Ошибка при обновлении: ${response.status} ${errorData}`);
      }
      
      toast.success('Данные велосипеда обновлены!');
      handleRefreshData();
      setShowEditDialog(false);
    } catch (error: any) {
      console.error('Ошибка при обновлении велосипеда:', error);
      toast.error(`Не удалось обновить данные: ${error.message}`);
    }
  };

  const handleDeleteBike = async (bike: Bike) => {
    try {
      const response = await fetch(`${SUPABASE_URL}/rest/v1/bikes?id=eq.${bike.id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_PUBLISHABLE_KEY,
          'Authorization': `Bearer ${SUPABASE_PUBLISHABLE_KEY}`,
          'Prefer': 'return=minimal'
        }
      });
      
      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(`Ошибка при удалении: ${response.status} ${errorData}`);
      }
      
      setBikes(bikes.filter(b => b.id !== bike.id));
      toast.success('Велосипед удален!');
    } catch (error: any) {
      console.error('Ошибка при удалении велосипеда:', error);
      toast.error(`Не удалось удалить велосипед: ${error.message}`);
    }
  };

  useEffect(() => {
    loadBikesData();
  }, [loadBikesData]);

  return {
    bikes: bikes,
    allBikes,
    loading,
    statsLoading,
    selectedBike,
    showAddDialog,
    showEditDialog,
    statusFilter,
    movementFilter,
    connectionError,
    dataSource,
    totalCount,
    setSelectedBike,
    setShowAddDialog,
    setShowEditDialog,
    setStatusFilter,
    setMovementFilter,
    setSearchQuery: handleSearchQueryChange,
    handleRefreshData,
    handleAddBike,
    handleEditBike,
    handleDeleteBike
  };
}
